import React, { useCallback, useMemo, useState } from 'react';
import { IconButton, Menu } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import MoreVert from '@mui/icons-material/MoreVert';
import { useI18n } from '../../lib/hook/useI18n';
import {
  SECTION_HEADER_SIDEBAR_KEBAB_MENU_ANCHOR_ORIGIN,
  SECTION_HEADER_SIDEBAR_KEBAB_MENU_TRANSFORM_ORIGIN,
  SectionHeaderMenuItem,
} from '../Room/components/Edit/SectionHeaderMenuShared';

type FolderViewMenuRowDef = {
  icon: React.ReactNode;
  key: string;
  messageKey: string;
  run: () => void;
  destructive?: boolean;
};

export interface FolderViewKebabMenuProps {
  onDelete: () => void;
  onRename?: () => void;
}

/** Kebab menu for folder view (add subfolder, rename, delete). */
const FolderViewKebabMenu: React.FC<FolderViewKebabMenuProps> = ({
  onDelete,
  onRename,
}) => {
  const { t } = useI18n();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  }, []);

  const menuRows = useMemo<FolderViewMenuRowDef[]>(() => {
    const rows: FolderViewMenuRowDef[] = [];

    if (onRename) {
      rows.push({
        key: 'rename',
        icon: <DriveFileRenameOutlineIcon fontSize="small" />,
        messageKey: 'room.folder.view.menu.rename',
        run: onRename,
      });
    }

    rows.push({
      key: 'delete',
      icon: <DeleteOutlineIcon fontSize="small" />,
      messageKey: 'room.folder.view.menu.delete',
      run: onDelete,
      destructive: true,
    });

    return rows;
  }, [onDelete, onRename]);

  return (
    <>
      <IconButton
        aria-haspopup="true"
        aria-label={t('room.folder.view.menu.open_aria')}
        onClick={handleOpen}
        size="small"
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={SECTION_HEADER_SIDEBAR_KEBAB_MENU_ANCHOR_ORIGIN}
        open={open}
        onClose={handleClose}
        transformOrigin={SECTION_HEADER_SIDEBAR_KEBAB_MENU_TRANSFORM_ORIGIN}
      >
        {menuRows.map((row) => (
          <SectionHeaderMenuItem
            key={row.key}
            destructive={row.destructive}
            icon={row.icon}
            label={t(row.messageKey)}
            onClick={() => {
              handleClose();
              row.run();
            }}
          />
        ))}
      </Menu>
    </>
  );
};

export default FolderViewKebabMenu;
